// ====================================
// MAIN API FUNCTION FOR HOTEL SUBMISSION
// ====================================

import { apiSend, getRoute } from "@services/axios";
import { store } from "@services/redux/store";

/**
 * Main function to handle hotel product submissions by step level
 * @param {Object} productData - Product data for the current step
 * @param {number} level - Step level (1, 2, or 3)
 * @returns {Promise} API response
 */
export const postHotel = async (productId = null, productData, level) => {
  switch (level) {
    case 1:
      return await postHotelStep1(productData, level);

    case 2:
      return await postHotelStep2(productId, productData, level);

    case 3:
      return await postHotelStep3(productId, productData, level);

    default:
      throw new Error(`Unsupported level: ${level} for hotel product`);
  }
};

export default postHotel;

// ====================================
// STEP 1 - CREATE HOTEL PRODUCT
// ====================================

export const postHotelStep1 = async (productData, level) => {
  try {
    const url = `${getRoute("create_hotel_step_1")}`;
    const response = await apiSend(url, "post", productData);
 
    handleBackendErrors(response.data);
    return response.data.result;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// STEP 2 - UPDATE HOTEL DETAILS + ROOMS
// ====================================

export const postHotelStep2 = async (product_id, productData, level) => {
  const productType = "Hotel";
  productData = store.getState()[productType.toLowerCase()]?.step2;
  console.log('hotel step 2',productData)
  try {
    const url = `${getRoute("create_hotel_step_2")}/${product_id}`;
    const response = await apiSend(url, "post", productData);
    console.log(response)
    handleBackendErrors(response.data);
    return response.data;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// STEP 3 - FINAL PRICING + POLICIES
// ====================================

export const postHotelStep3 = async (product_id, productData, level) => {
  localStorage.setItem("product_id_save", product_id);
  try {
    const productType = "Hotel";
    productData = store.getState()[productType.toLowerCase()]?.step3;
    console.log(productData)
    const url = `${getRoute("create_hotel_step_3")}/${product_id}`;
    const response = await apiSend(url, "post", productData);
    console.log(response)
    handleBackendErrors(response.data);
    return response.data;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// HELPER FUNCTIONS
// ====================================

/**
 * Detects and throws an error if the backend response indicates a problem.
 * If "invalid request data" is found, logs the backend error table.
 */
function handleBackendErrors(data) {
  if (!data) return;

  const backendMsg = data?.request?.message;
  const backendError = data?.result?.Error;

  // Case 1: Invalid request data — print the backend error table
  if (backendMsg && backendMsg.toLowerCase().includes("invalid request data")) {
    if (backendError && typeof backendError === "object") {
      console.table(flattenBackendErrors(backendError));
    }
    throw new Error(backendMsg);
  }

  // Case 2: Generic backend Error object
  if (backendError && typeof backendError === "object") {
    const firstKey = Object.keys(backendError)[0];
    const firstMsg = backendError[firstKey]?.[0] || "Unknown backend error";
    throw new Error(firstMsg);
  }
}

/**
 * Formats caught backend/network errors for consistent error throwing.
 */
function formatBackendError(error) {
  if (error?.response?.data) {
    handleBackendErrors(error.response.data);
  }
  return new Error(
    error.message || "An unknown error occurred during hotel submission."
  );
}

/**
 * Converts nested backend error objects to a flat list suitable for console.table.
 * Example:
 * {
 *   "familyRomantiqueService.0.equipements": ["Field is required"]
 * }
 * → [{ field: "familyRomantiqueService.0.equipements", message: "Field is required" }]
 */
function flattenBackendErrors(errorObj) {
  const rows = [];
  for (const key in errorObj) {
    const value = errorObj[key];
    if (Array.isArray(value)) {
      value.forEach((msg) => rows.push({ field: key, message: msg }));
    } else if (typeof value === "object") {
      for (const subKey in value) {
        rows.push({ field: `${key}.${subKey}`, message: value[subKey] });
      }
    } else {
      rows.push({ field: key, message: value });
    }
  }
  return rows;
}
